import { ActionContext, ActionTree } from 'vuex';
import { RootState } from '@/store';
import { LobbyState } from '@/store/modules/lobby/index';
import {
  CHANGE_PASSWORD,
  GET_HELP_DESK_DETAILS,
  GET_CHANGES_NOTIFICATION_FLAG,
  GET_ORG_NEWS,
  GET_USER_DETAILS,
  LOBBY_ACCESSIBLE,
  LOBBY_ROOT,
  MUST_CHANGE_PASSWORD,
  PERFORM_DATA_CLEANSING,
  SET_CHANGE_PASSWORD_ERRORS,
  SET_CHANGE_PASSWORD_LOADING,
  SET_CHANGE_USER_DATA_CLEANSING_LOADING,
  SET_HELP_DESK_DETAILS,
  SET_LOBBY_SUCCESS_DIALOG,
  SET_MUST_PERFORM_DATA_CLEANSING,
  SET_OPEN_POPUP_CHANGES_NOTIFICATION_FLAG,
  SET_ORG_NEWS,
  SET_PERFORM_CHANGE_PASSWORD,
  SET_PERFORM_DATA_CLEANSING_ERRORS,
  SET_USER_DATA_CLEANSING_ERRORS,
  SET_USER_PERFORM_DATA_CLEANSING,
  UPDATE_USER_DETAILS,
} from '@/types/lobby.types';
import LobbyService from '@/services/lobby.service';
import { RequestPayload } from '@/models/base/RequestPayload';
import { eventsHub } from '@/main';
import { AUTHENTICATION_ROOT, SET_USER_DETAILS, USER_DETAILS } from '@/types/authentication.types';
import { finalize } from 'rxjs/operators';
import {
  AUTHORIZATION_ROOT,
  SET_TEMPORARY_TOKENS,
  SET_TOKENS,
  TEMPORARY_ACCESS_TOKEN,
} from '@/types/authorization.types';


export const lobbyService = new LobbyService();

export const actions: ActionTree<LobbyState, RootState> = {

  [GET_ORG_NEWS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<LobbyState, RootState>,
  ) => {
    lobbyService.getOrgNews()
      .subscribe(
        (data) => {
          commit(SET_ORG_NEWS, data.empMessages);
        },
      );
  },

  [GET_HELP_DESK_DETAILS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<LobbyState, RootState>,
  ) => {
    lobbyService.getHelpDeskDetails()
      .subscribe(
        (data) => {
          commit(SET_HELP_DESK_DETAILS, data);
        },
      );
  },

  [GET_USER_DETAILS]: (
    { commit, dispatch, state , rootGetters}: ActionContext<LobbyState, RootState>,
  ) => {
    commit(SET_CHANGE_PASSWORD_LOADING, false);
    commit(SET_CHANGE_USER_DATA_CLEANSING_LOADING, false);
    const temporaryAccessToken = rootGetters[AUTHORIZATION_ROOT + TEMPORARY_ACCESS_TOKEN];
    return new Promise((resolve) => {
      lobbyService.getUserDetails(temporaryAccessToken)
        .pipe(
          finalize(() => {
            commit(SET_CHANGE_PASSWORD_LOADING, false);
            commit(SET_CHANGE_USER_DATA_CLEANSING_LOADING, false);
          }),
        )
        .subscribe(
          data => {
            resolve(data);
          },
          error => commit(SET_CHANGE_PASSWORD_ERRORS, error.messages),
        );
    });
  },

  [CHANGE_PASSWORD]: (
    { commit, dispatch, state }: ActionContext<LobbyState, RootState>,
    { requestBody }: RequestPayload,
  ) => {
    lobbyService.changePassword(requestBody)
      .pipe(
        finalize(() => commit(SET_CHANGE_PASSWORD_LOADING, false)),
      )
      .subscribe(
        data => {
          commit(SET_LOBBY_SUCCESS_DIALOG, true);
          commit(SET_PERFORM_CHANGE_PASSWORD, false);
          eventsHub.$router.push({ name: 'lobby' });
        },
        error => commit(SET_CHANGE_PASSWORD_ERRORS, error.messages),
      );
  },

  [PERFORM_DATA_CLEANSING]: (
    { commit, dispatch, state, rootGetters }: ActionContext<LobbyState, RootState>,
    { requestBody }: RequestPayload,
  ) => {
    const temporaryAccessToken = rootGetters[AUTHORIZATION_ROOT + TEMPORARY_ACCESS_TOKEN];
    lobbyService.performDataCleansing(requestBody, temporaryAccessToken)
      .subscribe(
        (data: any) => {
          const userDetails = rootGetters[AUTHENTICATION_ROOT + USER_DETAILS];
          commit(AUTHORIZATION_ROOT + SET_TOKENS, {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
          }, { root: true });
          commit(AUTHORIZATION_ROOT + SET_TEMPORARY_TOKENS, {
            accessToken: null,
            refreshToken: null,
          }, { root: true });

          eventsHub.$router.push({ name: 'lobby' });
          commit(SET_LOBBY_SUCCESS_DIALOG, true);

          userDetails.email = requestBody.email;
          userDetails.phoneNumber = requestBody.phone;
          commit(AUTHENTICATION_ROOT + SET_USER_DETAILS, userDetails, { root: true });
          commit(LOBBY_ROOT + SET_MUST_PERFORM_DATA_CLEANSING,
            false, { root: true });
        },
        error => commit(SET_PERFORM_DATA_CLEANSING_ERRORS, error.messages),
      );
  },

  [UPDATE_USER_DETAILS]: (
    { commit, dispatch, state }: ActionContext<LobbyState, RootState>,
    { requestBody }: RequestPayload,
  ) => {
    lobbyService.updateUserDetails(requestBody)
      .pipe(
        finalize(() => commit(SET_CHANGE_USER_DATA_CLEANSING_LOADING, false)),
      )
      .subscribe(
        (data: any) => {
          commit(AUTHENTICATION_ROOT + SET_USER_DETAILS,
            {
              email: requestBody.email,
              phoneNumber: requestBody.phone,
            }, { root: true });
          commit(SET_USER_PERFORM_DATA_CLEANSING, false);
          commit(SET_LOBBY_SUCCESS_DIALOG, true);
          eventsHub.$router.push({ name: 'lobby' });
        },
        error => commit(SET_USER_DATA_CLEANSING_ERRORS, error.messages),
      );
  },

  [LOBBY_ACCESSIBLE]: () => {
    return new Promise((resolve, reject) => {
      lobbyService.lobbyAccessible()
        .subscribe(
          () => resolve(true),
          () => reject(false),
        );
    });
  },

  [GET_CHANGES_NOTIFICATION_FLAG]: (
    { commit, dispatch, state, rootGetters }: ActionContext<LobbyState, RootState>) => {
    lobbyService.getChangesNotificationFlag()
      .subscribe(
        (data: any) => {
          commit(SET_OPEN_POPUP_CHANGES_NOTIFICATION_FLAG, data.body);
        },
      );
  },

};
